/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Stack,
    Text,
    Box,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useItemVariant} from '@salesforce/retail-react-app/app/components/item-variant'
import {useProducts, useProduct} from '@salesforce/commerce-sdk-react'
import {getDisplayVariationValues} from '@salesforce/retail-react-app/app/utils/product-utils'

/**
 * In the context of a cart product item variant, this component renders a styled
 * list of the selected variation values as well as any promos (w/ info popover).
 */
const ItemAttributes = ({
    includeQuantity,
    excludeBonusLabel,
    hideAttributeLabels = false,
    ...props
}) => {
    const cartStyles = useMultiStyleConfig('CartStyles')
    const variant = useItemVariant()
    const intl = useIntl()

    const displayBonusProductLabel = !excludeBonusLabel && variant?.bonusProductLineItem

    const variationValues = getDisplayVariationValues(
        variant?.variationAttributes,
        variant?.variationValues
    )

    // get variant info for bundled products in cart page and order history page
    const productBundleIds =
        variant?.bundledProductItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: productBundleVariantData} = useProducts(
        {
            parameters: {
                ids: productBundleIds,
                allImages: false
            }
        },
        {
            enabled: Boolean(variant?.type?.bundle && productBundleIds),
            select: (result) => {
                // formats response so we can easily display child quantity/variant selection
                return result?.data?.map((item) => {
                    const quantity = variant?.bundledProductItems.find(
                        (childProduct) => childProduct.productId === item.id
                    )?.quantity
                    return {
                        ...item,
                        quantity,
                        variationValues: getDisplayVariationValues(
                            item?.variationAttributes,
                            item?.variationValues
                        )
                    }
                })
            }
        }
    )

    // get bundle product data for wishlist page
    const {data: productBundleData, isLoading: productBundleIsLoading} = useProduct(
        {
            parameters: {
                id: variant?.id,
                allImages: false
            }
        },
        {
            enabled: Boolean(variant?.type?.bundle && !productBundleIds)
        }
    )
    const renderVariationsWrapper =
        displayBonusProductLabel ||
        !!Object?.keys(variationValues)?.length ||
        includeQuantity ||
        (!productBundleIsLoading && productBundleData && !productBundleVariantData)

    if (!renderVariationsWrapper) {
        return null
    }

    return (
        <Stack spacing={1.5} flex={1} {...props} width="fit-content" className="variations-stack">
            {displayBonusProductLabel && (
                <Text lineHeight={1} color="black.600" fontSize="sm">
                    <FormattedMessage
                        defaultMessage="Bonus Product"
                        id="item_attributes.label.bonus_product"
                    />
                </Text>
            )}

            {variationValues &&
                Object.keys(variationValues).map((key) => (
                    <Text
                        key={`${key}: ${variationValues[key]}`}
                        {...cartStyles.lineItems.item.variationsWrapper.text}
                    >
                        {hideAttributeLabels
                            ? variationValues[key]
                            : `${key}: ${variationValues[key]}`}
                    </Text>
                ))}

            {includeQuantity && (
                <Text className="product-quantity" lineHeight={1} color="gray.700" fontSize="sm">
                    <FormattedMessage
                        defaultMessage="Quantity: {quantity}"
                        values={{quantity: variant.quantity}}
                        id="item_attributes.label.quantity"
                    />
                </Text>
            )}

            {!productBundleIsLoading && productBundleData && !productBundleVariantData && (
                <Box>
                    {productBundleData?.bundledProducts.map(({product, quantity}) => (
                        <Box marginTop={2} key={product.id}>
                            <Text fontSize="sm" color="gray.700" as="b">
                                {product?.name}
                            </Text>
                            <Text fontSize="sm" color="gray.700">
                                {intl.formatMessage({
                                    defaultMessage: 'Qty',
                                    id: 'add_to_cart_modal.label.quantity'
                                })}
                                : {quantity}
                            </Text>
                        </Box>
                    ))}
                </Box>
            )}
        </Stack>
    )
}

ItemAttributes.propTypes = {
    includeQuantity: PropTypes.bool,
    excludeBonusLabel: PropTypes.bool,
    hideAttributeLabels: PropTypes.bool
}

export default ItemAttributes
